import { RecordCollection } from '../../app/record-collection.js';
import { CollectionViewPicker } from '../../app/collection-view.js';
import type { PaymentInstructions } from '@hue/contracts';
import { PaymentInstructionsFields } from './payment-instructions.js';
import { useFinancialClassification } from '../../app/financial-classification-field.js';
import {
  Button,
  CurrencyInput,
  Input,
  Modal,
  MonthPicker,
  Select,
  StatusPill,
  formatCurrency,
  type CivilDate,
  type StatusTone,
  useSnackbar,
  IconButton,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PencilLine, Plus, Repeat2, Ban, Undo2, Archive, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import {
  financeAccountsApi,
  financialRecurrencesApi,
  getApiError,
  getDuplicateSuspects,
  type DuplicateSuspect,
  type FinanceAccount,
  type FinancialRecurrence,
  type FinancialRecurrenceAmountPolicy,
  type FinancialRecurrenceInput,
  type FinancialRecurrenceResolution,
} from '../../lib/api.js';
import {
  ListFilterBar,
  EmptyCard,
  ErrorCard,
  LoadingCard,
  RequestAlert,
} from '../../app/data-view.js';
import { DuplicateSuspicionDialog } from './duplicate-suspicion-dialog.js';
import { generationNotice } from './recurrence-generation-notice.js';

export interface RecurrenceCapabilities {
  canRead: boolean;
  canClassify?: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canArchive: boolean;
  canGenerate: boolean;
}
/// A competência é sempre o primeiro dia do mês; o seletor de mês trabalha em `AAAA-MM`.
const competenceOf = (value: string): CivilDate => `${value.slice(0, 7)}-01`;
const monthOf = (value: string | null) => (value ? value.slice(0, 7) : null);
function resolutionOf(item: FinancialRecurrence): FinancialRecurrenceResolution {
  return item.resolution ?? { status: 'PENDING' };
}
function resolutionLabel(
  item: FinancialRecurrence,
): { tone: StatusTone; label: string } | undefined {
  const resolution = resolutionOf(item);
  if (resolution.status === 'MATERIALIZED')
    return resolution.canceled
      ? { tone: 'neutral', label: 'Lançada e cancelada' }
      : { tone: 'positive', label: 'Lançada no mês' };
  if (resolution.status === 'WAIVED') return { tone: 'neutral', label: 'Mês dispensado' };
  if (!item.active) return undefined;
  return item.amountPolicy === 'VARIABLE'
    ? { tone: 'warning', label: 'Aguardando valor' }
    : { tone: 'progress', label: 'Prevista' };
}

export function CompanyRecurrences({
  month,
  capabilities,
  onClose,
}: {
  month: CivilDate;
  capabilities: RecurrenceCapabilities;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const snackbar = useSnackbar();
  const competence = competenceOf(month);
  const [search, setSearch] = useState('');
  const [activity, setActivity] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [policy, setPolicy] = useState<FinancialRecurrenceAmountPolicy | 'ALL'>('ALL');
  const [editing, setEditing] = useState<FinancialRecurrence | null | undefined>();
  const [generating, setGenerating] = useState<FinancialRecurrence>();
  const [waiving, setWaiving] = useState<FinancialRecurrence>();
  const [activating, setActivating] = useState<FinancialRecurrence>();
  const [batchOpen, setBatchOpen] = useState(false);
  const [actionError, setActionError] = useState<string>();
  const accounts = useQuery({
    queryKey: ['company', 'finance-accounts', 'active'],
    queryFn: () =>
      financeAccountsApi.list({
        activity: ['ACTIVE'],
        sort: 'displayName',
        direction: 'asc',
      }),
    retry: false,
  });
  const recurrences = useQuery({
    queryKey: ['company', 'financial-recurrences', competence, search, activity, policy],
    queryFn: () =>
      financialRecurrencesApi.list({
        month: competence,
        ...(search.trim() ? { search: search.trim() } : {}),
        activity,
        policy,
        sort: 'counterpartyName',
        direction: 'asc',
      }),
    retry: false,
  });
  async function refresh(message: string, tone: 'positive' | 'info' = 'positive') {
    await client.invalidateQueries({ queryKey: ['company', 'financial-recurrences'] });
    await client.invalidateQueries({ queryKey: ['company', 'financial-obligations'] });
    setEditing(undefined);
    setGenerating(undefined);
    setWaiving(undefined);
    setActivating(undefined);
    setActionError(undefined);
    snackbar.show(message, tone);
  }
  const afterGeneration = (base: string, generation: FinancialRecurrence['generation']) => {
    const notice = generationNotice(base, generation);
    return refresh(notice.text, notice.tone);
  };
  const pendingVariables = (recurrences.data ?? []).filter(
    (item) =>
      item.active && item.amountPolicy === 'VARIABLE' && resolutionOf(item).status === 'PENDING',
  );
  function recurrenceActions(item: FinancialRecurrence) {
    const resolution = resolutionOf(item);
    return (
      <div className="flex flex-wrap gap-1">
        {capabilities.canGenerate && item.active && resolution.status === 'PENDING' ? (
          <>
            <IconButton label="Lançar mês" icon={<Plus />} onClick={() => setGenerating(item)} />
            <IconButton label="Dispensar mês" icon={<Ban />} onClick={() => setWaiving(item)} />
          </>
        ) : null}
        {capabilities.canGenerate && resolution.status === 'WAIVED' ? (
          <IconButton
            label="Desfazer dispensa"
            icon={<Undo2 />}
            onClick={() =>
              void financialRecurrencesApi
                .revokeWaiver(item.id, competence)
                .then(() => refresh('Dispensa desfeita.'))
                .catch((error) => setActionError(getApiError(error)))
            }
          />
        ) : null}
        {capabilities.canUpdate ? (
          <IconButton label="Editar" icon={<PencilLine />} onClick={() => setEditing(item)} />
        ) : null}
        {capabilities.canArchive ? (
          <IconButton
            label={item.active ? 'Arquivar' : 'Reativar'}
            icon={item.active ? <Archive /> : <RotateCcw />}
            tone={item.active ? 'danger' : 'neutral'}
            onClick={() => setActivating(item)}
          />
        ) : null}
      </div>
    );
  }
  return (
    <>
      <Modal
        open
        onOpenChange={onClose}
        footer={
          <>
            {capabilities.canGenerate && pendingVariables.length > 0 ? (
              <Button variant="outline" onClick={() => setBatchOpen(true)}>
                Informar valores do mês
              </Button>
            ) : null}
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>
          </>
        }
        title="Despesas recorrentes"
        description={`Vigentes em ${competence.slice(0, 7)}. Despesas fixas são lançadas automaticamente. Informe os valores variáveis juntos; lançar não significa pagar.`}
      >
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <ListFilterBar
              variant="panel"
              searchLabel="Buscar recorrência"
              search={search}
              onSearchChange={setSearch}
              count={`${recurrences.data?.length ?? 0} ${(recurrences.data?.length ?? 0) === 1 ? 'recorrência' : 'recorrências'}`}
            >
              <div className="w-full sm:w-44">
                <Select
                  label="Situação"
                  value={activity}
                  options={[
                    { value: 'ALL', label: 'Todas' },
                    { value: 'ACTIVE', label: 'Ativas' },
                    { value: 'INACTIVE', label: 'Arquivadas' },
                  ]}
                  onValueChange={(value) => setActivity(value as typeof activity)}
                />
              </div>
              <div className="w-full sm:w-44">
                <Select
                  label="Valor"
                  value={policy}
                  options={[
                    { value: 'ALL', label: 'Todos' },
                    { value: 'FIXED', label: 'Fixo vigente' },
                    { value: 'VARIABLE', label: 'Variável mensal' },
                  ]}
                  onValueChange={(value) => setPolicy(value as typeof policy)}
                />
              </div>
              {capabilities.canCreate ? (
                <Button className="self-end" onClick={() => setEditing(null)}>
                  <Plus className="size-4" />
                  Nova
                </Button>
              ) : null}
            </ListFilterBar>
          </div>
          <CollectionViewPicker />
        </div>
        {recurrences.isLoading || accounts.isLoading ? (
          <LoadingCard label="Carregando despesas recorrentes" />
        ) : recurrences.error || accounts.error ? (
          <ErrorCard message={getApiError(recurrences.error ?? accounts.error)} />
        ) : recurrences.data?.length === 0 ? (
          <EmptyCard
            icon={Repeat2}
            title="Nenhuma recorrência encontrada"
            description="Cadastre aluguel, água, energia, tributos e outras despesas mensais."
          />
        ) : (
          <RecordCollection
            label="Despesas recorrentes"
            records={recurrences.data ?? []}
            recordKey={(item) => item.id}
            columns={[
              {
                key: 'description',
                label: 'Despesa',
                value: (item) => item.description,
                render: (item) => <strong>{item.description}</strong>,
              },
              {
                key: 'counterparty',
                label: 'Favorecido',
                value: (item) => item.counterpartyName,
                render: (item) => item.counterpartyName,
              },
              {
                key: 'day',
                label: 'Dia',
                value: (item) => item.dueDay,
                render: (item) => item.dueDay,
              },
              {
                key: 'amount',
                label: 'Valor',
                value: (item) => item.fixedAmountCents ?? 0,
                render: (item) =>
                  item.amountPolicy === 'FIXED'
                    ? `${formatCurrency(item.fixedAmountCents!, item.currency)} até reajuste`
                    : 'valor informado todo mês',
              },
              {
                key: 'status',
                label: 'Situação',
                render: (item) => {
                  const resolution = resolutionOf(item);
                  return (
                    <div className="grid gap-1">
                      <StatusPill tone={item.active ? 'positive' : 'neutral'}>
                        {item.active ? 'Ativa' : 'Arquivada'}
                      </StatusPill>
                      {resolutionLabel(item) ? (
                        <StatusPill tone={resolutionLabel(item)!.tone}>
                          {resolutionLabel(item)!.label}
                        </StatusPill>
                      ) : null}
                      {resolution.status === 'WAIVED' ? (
                        <span className="text-xs text-content-muted">
                          Dispensa de {competence.slice(0, 7)}: {resolution.reason}
                        </span>
                      ) : null}
                    </div>
                  );
                },
              },
            ]}
            actions={recurrenceActions}
            card={(item) => {
              const resolution = resolutionOf(item);
              const pill = resolutionLabel(item);
              return (
                <article
                  key={item.id}
                  className="rounded-xl border border-surface-border bg-surface-soft p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <strong>{item.description}</strong>
                        <StatusPill tone={item.active ? 'positive' : 'neutral'}>
                          {item.active ? 'Ativa' : 'Arquivada'}
                        </StatusPill>
                        {pill ? <StatusPill tone={pill.tone}>{pill.label}</StatusPill> : null}
                      </div>
                      <p className="mt-1 text-sm text-content-muted">
                        {item.counterpartyName} · vence dia {item.dueDay} ·{' '}
                        {item.amountPolicy === 'FIXED'
                          ? formatCurrency(item.fixedAmountCents!, item.currency) + ' até reajuste'
                          : 'valor informado todo mês'}
                      </p>
                      {resolution.status === 'WAIVED' ? (
                        <p className="mt-1 text-sm text-content-muted">
                          Dispensa de {competence.slice(0, 7)}: {resolution.reason}
                        </p>
                      ) : null}
                    </div>
                    {recurrenceActions(item)}
                  </div>
                </article>
              );
            }}
          />
        )}
        {capabilities.canGenerate &&
        recurrences.data?.some(
          (item) =>
            item.active && item.amountPolicy === 'FIXED' && resolutionOf(item).status === 'PENDING',
        ) ? (
          <p className="mt-3 text-sm text-content-muted">
            Fixas pendentes podem estar aguardando a rotina automática ou revisão de um lançamento
            parecido. Use Lançar mês para revisar.
          </p>
        ) : null}
        {actionError ? <RequestAlert message={actionError} /> : null}
      </Modal>
      {batchOpen ? (
        <VariableRecurrenceBatch
          items={pendingVariables}
          month={competence}
          onClose={() => {
            setBatchOpen(false);
            void Promise.all([
              client.invalidateQueries({ queryKey: ['company', 'financial-recurrences'] }),
              client.invalidateQueries({ queryKey: ['company', 'financial-obligations'] }),
            ]);
          }}
          onSaved={async () => {
            setBatchOpen(false);
            await refresh('Valores informados. Despesas disponíveis para conciliação.');
          }}
        />
      ) : null}
      {editing !== undefined ? (
        <RecurrenceForm
          canClassify={capabilities.canClassify}
          item={editing}
          accounts={accounts.data ?? []}
          competence={competence}
          onClose={() => setEditing(undefined)}
          onSaved={(generation) =>
            editing
              ? refresh('Recorrência atualizada.')
              : afterGeneration('Recorrência cadastrada.', generation)
          }
        />
      ) : null}
      {generating ? (
        <GenerateForm
          item={generating}
          month={competence}
          onClose={() => setGenerating(undefined)}
          onSaved={(message) => refresh(message ?? 'Despesa do mês disponível em Despesas.')}
        />
      ) : null}
      {waiving ? (
        <WaiverForm
          item={waiving}
          month={competence}
          onClose={() => setWaiving(undefined)}
          onSaved={() => refresh('Competência dispensada.')}
        />
      ) : null}
      {activating ? (
        <ActivationForm
          item={activating}
          competence={competence}
          onClose={() => setActivating(undefined)}
          onSaved={(generation) =>
            activating.active
              ? refresh('Recorrência arquivada.')
              : afterGeneration('Recorrência reativada.', generation)
          }
        />
      ) : null}
    </>
  );
}

function RecurrenceForm({
  item,
  canClassify = false,
  accounts,
  competence,
  onClose,
  onSaved,
}: {
  canClassify?: boolean | undefined;
  item: FinancialRecurrence | null;
  accounts: FinanceAccount[];
  competence: CivilDate;
  onClose: () => void;
  onSaved: (generation?: FinancialRecurrence['generation']) => Promise<void>;
}) {
  const classification = useFinancialClassification(canClassify, 'RECURRENCE', item?.id);
  const initial = accounts.find((account) => account.id === item?.financeAccountId) ?? accounts[0];
  const [accountId, setAccountId] = useState(initial?.id ?? '');
  const [paymentInstructions, setPaymentInstructions] = useState<PaymentInstructions | null>(
    item?.paymentInstructions ?? null,
  );
  const [counterparty, setCounterparty] = useState(item?.counterpartyName ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [dueDay, setDueDay] = useState(String(item?.dueDay ?? 10));
  const [policy, setPolicy] = useState<FinancialRecurrenceAmountPolicy>(
    item?.amountPolicy ?? 'VARIABLE',
  );
  const [amount, setAmount] = useState<number | null>(item?.fixedAmountCents ?? null);
  const [startsOn, setStartsOn] = useState<string | null>(competence.slice(0, 7));
  const [endsOn, setEndsOn] = useState<string | null>(monthOf(item?.endsOn ?? null));
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [suspects, setSuspects] = useState<DuplicateSuspect[]>([]);
  const account = accounts.find((one) => one.id === accountId);
  function inputOf(): FinancialRecurrenceInput | undefined {
    const day = Number(dueDay);
    if (
      !account ||
      counterparty.trim().length < 2 ||
      description.trim().length < 2 ||
      !startsOn ||
      !Number.isInteger(day) ||
      day < 1 ||
      day > 31 ||
      (policy === 'FIXED' && !amount)
    ) {
      setError(
        'Preencha conta prevista, contraparte, descrição, vencimento, vigência e o valor quando ele for fixo.',
      );
      return undefined;
    }
    return {
      ...classification.options,
      paymentInstructions,
      financeAccountId: account.id,
      counterpartyName: counterparty,
      description,
      dueDay: day,
      amountPolicy: policy,
      fixedAmountCents: policy === 'FIXED' ? amount : null,
      currency: account.currency,
      startsOn: competenceOf(startsOn),
      endsOn: endsOn ? competenceOf(endsOn) : null,
    };
  }
  async function submit(input: FinancialRecurrenceInput, duplicateReason?: string) {
    if (item) {
      await financialRecurrencesApi.update(item.id, input);
      await onSaved();
    } else await onSaved((await financialRecurrencesApi.create(input, duplicateReason)).generation);
  }
  async function save() {
    if (!classification.ready) {
      setError('Escolha a conta do plano ou decida salvar sem conta.');
      return;
    }
    const input = inputOf();
    if (!input) return;
    setSaving(true);
    setError(undefined);
    try {
      await submit(input);
    } catch (cause) {
      const found = getDuplicateSuspects(cause);
      if (found.length > 0) setSuspects(found);
      else setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={onClose}
      title={item ? 'Editar recorrência' : 'Nova despesa recorrente'}
      description={
        item
          ? 'As condições novas valem da competência escolhida em diante; os meses anteriores conservam a versão que estava valendo.'
          : 'A vigência começa na competência escolhida; cada alteração posterior nasce como uma nova versão.'
      }
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Voltar
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Salvar recorrência
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {classification.field}
        <Select
          label="Conta prevista"
          value={accountId}
          options={accounts.map((one) => ({ value: one.id, label: one.displayName }))}
          onValueChange={setAccountId}
        />
        <Select
          label="Comportamento do valor"
          value={policy}
          options={[
            { value: 'VARIABLE', label: 'Informar a cada mês' },
            { value: 'FIXED', label: 'Manter até reajuste' },
          ]}
          onValueChange={(value) => {
            setPolicy(value as FinancialRecurrenceAmountPolicy);
            if (value === 'VARIABLE') setAmount(null);
          }}
        />
        <Input
          label="Contraparte"
          value={counterparty}
          maxLength={160}
          onChange={(event) => setCounterparty(event.target.value)}
        />
        <Input
          label="Descrição"
          value={description}
          maxLength={240}
          onChange={(event) => setDescription(event.target.value)}
        />
        <Input
          label="Dia do vencimento"
          inputMode="numeric"
          value={dueDay}
          onChange={(event) => setDueDay(event.target.value)}
        />
        {policy === 'FIXED' ? (
          <CurrencyInput
            label="Valor vigente"
            currency={account?.currency ?? 'BRL'}
            value={amount}
            onValueChange={setAmount}
          />
        ) : (
          <div className="rounded-xl border border-surface-border bg-surface-soft p-3 text-sm text-content-muted">
            O valor será solicitado ao lançar cada mês.
          </div>
        )}
        <MonthPicker
          variant="field"
          clearable={false}
          label={item ? 'Vigente a partir de' : 'Início da vigência'}
          hint={
            item
              ? 'Competências anteriores continuam com as condições que já estavam valendo.'
              : undefined
          }
          value={startsOn}
          onValueChange={setStartsOn}
        />
        <MonthPicker
          variant="field"
          label="Fim da vigência (opcional)"
          emptyLabel="Sem término"
          value={endsOn}
          onValueChange={setEndsOn}
        />
      </div>
      <PaymentInstructionsFields
        value={paymentInstructions}
        onChange={setPaymentInstructions}
        recurring
      />
      {error ? <RequestAlert message={error} /> : null}

      {suspects.length > 0 ? (
        <DuplicateSuspicionDialog
          suspects={suspects}
          title="Recorrência parecida já cadastrada"
          description="Duas famílias iguais passariam a gerar duas despesas todo mês. Explique por que as duas devem existir."
          confirmLabel="Cadastrar mesmo assim"
          onConfirm={async (reason) => {
            const input = inputOf();
            if (input) await submit(input, reason);
          }}
          onClose={() => setSuspects([])}
        />
      ) : null}
    </Modal>
  );
}
function ActivationForm({
  item,
  competence,
  onClose,
  onSaved,
}: {
  item: FinancialRecurrence;
  competence: CivilDate;
  onClose: () => void;
  onSaved: (generation?: FinancialRecurrence['generation']) => Promise<void>;
}) {
  const [month, setMonth] = useState(competence.slice(0, 7));
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    setError(undefined);
    try {
      const saved = await financialRecurrencesApi.setActive(
        item.id,
        !item.active,
        competenceOf(month),
      );
      await onSaved(saved.generation);
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={onClose}
      title={item.active ? `Arquivar ${item.description}` : `Reativar ${item.description}`}
      description={
        item.active
          ? 'As despesas já lançadas permanecem; a recorrência deixa de valer da competência escolhida em diante.'
          : 'A recorrência volta a valer da competência escolhida em diante, sem reabrir meses anteriores.'
      }
    >
      <MonthPicker
        variant="field"
        clearable={false}
        label="A partir da competência"
        value={month}
        onValueChange={(value) => setMonth(value ?? month)}
      />
      {error ? <RequestAlert message={error} /> : null}
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          Voltar
        </Button>
        <Button loading={saving} onClick={() => void save()}>
          {item.active ? 'Arquivar' : 'Reativar'}
        </Button>
      </div>
    </Modal>
  );
}
function WaiverForm({
  item,
  month,
  onClose,
  onSaved,
}: {
  item: FinancialRecurrence;
  month: CivilDate;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  async function save() {
    if (reason.trim().length < 3) {
      setError('Explique por que este mês não terá despesa.');
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      await financialRecurrencesApi.waive(item.id, month, reason);
      await onSaved();
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={onClose}
      title={`Dispensar ${item.description}`}
      description={`A competência ${month.slice(0, 7)} fica resolvida sem despesa e deixa de ser cobrada como pendência.`}
    >
      <Input
        label="Motivo da dispensa"
        value={reason}
        maxLength={240}
        onChange={(event) => setReason(event.target.value)}
      />
      {error ? <RequestAlert message={error} /> : null}
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          Voltar
        </Button>
        <Button loading={saving} onClick={() => void save()}>
          Dispensar mês
        </Button>
      </div>
    </Modal>
  );
}
function GenerateForm({
  item,
  month,
  onClose,
  onSaved,
}: {
  item: FinancialRecurrence;
  month: CivilDate;
  onClose: () => void;
  onSaved: (message?: string) => Promise<void>;
}) {
  const [amount, setAmount] = useState<number | null>(item.fixedAmountCents);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [suspects, setSuspects] = useState<DuplicateSuspect[]>([]);
  async function generate(duplicateReason?: string) {
    const result = await financialRecurrencesApi.generate(
      item.id,
      month,
      item.amountPolicy === 'VARIABLE' ? amount! : undefined,
      duplicateReason,
    );
    await onSaved();
    if (result.status === 'ALREADY_EXISTS') setError('Essa competência já estava lançada.');
  }
  async function save() {
    if (item.amountPolicy === 'VARIABLE' && !amount) {
      setError('Informe o valor desta competência.');
      return;
    }
    setSaving(true);
    try {
      await generate();
    } catch (cause) {
      const found = getDuplicateSuspects(cause);
      if (found.length > 0) setSuspects(found);
      else setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={onClose}
      title={`Lançar ${item.description}`}
      description={`Competência ${month.slice(0, 7)}; vencimento calculado pelo dia ${item.dueDay}.`}
    >
      {item.amountPolicy === 'VARIABLE' ? (
        <CurrencyInput
          label="Valor desta competência"
          currency={item.currency}
          value={amount}
          onValueChange={setAmount}
        />
      ) : (
        <p className="text-sm text-content-muted">
          Valor vigente: <strong>{formatCurrency(item.fixedAmountCents!, item.currency)}</strong>
        </p>
      )}
      {error ? <RequestAlert message={error} /> : null}
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          Voltar
        </Button>
        <Button loading={saving} onClick={() => void save()}>
          Gerar despesa
        </Button>
      </div>
      {suspects.length > 0 ? (
        <DuplicateSuspicionDialog
          suspects={suspects}
          title="Despesa parecida já existe neste mês"
          description={`Já existe lançamento parecido na competência ${month.slice(0, 7)}. Vincule o que já está lançado ou explique por que os dois devem existir.`}
          confirmLabel="Gerar mesmo assim"
          onLink={async (suspect) => {
            await financialRecurrencesApi.linkOccurrence(item.id, month, suspect.id);
            await onSaved('Despesa existente vinculada à recorrência.');
          }}
          onConfirm={(reason) => generate(reason)}
          onClose={() => setSuspects([])}
        />
      ) : null}
    </Modal>
  );
}

function VariableRecurrenceBatch({
  items,
  month,
  onClose,
  onSaved,
}: {
  items: FinancialRecurrence[];
  month: CivilDate;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [values, setValues] = useState<Record<string, number | null>>({});
  const [done, setDone] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  async function save() {
    const selected = items.filter((item) => !done.includes(item.id) && values[item.id] != null);
    if (!selected.length) {
      setErrors({ form: 'Informe pelo menos um valor.' });
      return;
    }
    if (selected.some((item) => !values[item.id] || values[item.id]! < 1)) {
      setErrors({ form: 'Os valores informados precisam ser maiores que zero.' });
      return;
    }
    setSaving(true);
    const failures: Record<string, string> = {};
    const completed = [...done];
    try {
      for (const item of selected) {
        try {
          await financialRecurrencesApi.generate(item.id, month, values[item.id]!);
          completed.push(item.id);
        } catch (error) {
          failures[item.id] = getDuplicateSuspects(error).length
            ? 'Há lançamento parecido. Feche esta janela e use Lançar mês nesta despesa para revisar.'
            : getApiError(error);
        }
      }
      setDone(completed);
      setErrors(failures);
      if (!Object.keys(failures).length) await onSaved();
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={() => {
        if (!saving) onClose();
      }}
      title="Valores das despesas recorrentes"
      description={`Competência ${month.slice(0, 7)}. Preencha as despesas conhecidas; campos vazios continuam aguardando valor. A lista respeita os filtros da janela anterior.`}
      footer={
        <>
          <Button variant="outline" disabled={saving} onClick={onClose}>
            Voltar
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Lançar valores informados
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {items.map((item) => (
          <div key={item.id} className="rounded-xl border border-surface-border p-3">
            <CurrencyInput
              label={`${item.description} · ${item.counterpartyName}`}
              currency={item.currency}
              value={values[item.id] ?? null}
              onValueChange={(value) => setValues((current) => ({ ...current, [item.id]: value }))}
              disabled={saving || done.includes(item.id)}
            />
            {done.includes(item.id) ? (
              <p role="status" className="text-sm text-positive">
                Despesa lançada.
              </p>
            ) : null}
            {errors[item.id] ? <RequestAlert message={errors[item.id]!} /> : null}
          </div>
        ))}
      </div>
      {errors.form ? <RequestAlert message={errors.form} /> : null}
    </Modal>
  );
}
